import moment from "moment";
import { Link, navigate } from "@reach/router";
import React, { useContext, useEffect, useState } from "react";
import { PlaybacksContext } from "../../context/PlaybacksContext";
import { ReservationsContext } from "../../context/ReservationsContext";
import { CustomerWorkoutPlansContext } from "../../context/CustomerWorkoutPlansContext";
import ProgressBar from "../common/ProgressBar";
import { setupTooltips } from "../../utils";
import { handleWorkoutPlanner } from "../../utils/workout_planner";
import InlineStreaks from "./InlineStreaks";
import useRewards from "../../hooks/useRewards";

const StreakWeek = ({
  start_date,
  end_date,
  hideHeader,
  weekIndex,
  modifier,
}) => {
  const [endDate, setEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [daysCompleted, setDaysCompleted] = useState(0);
  const [daysComponents, setDaysComponents] = useState(null);

  const { playbacks, getPlaybacksByDate } = useContext(PlaybacksContext);
  const { reservations, getMyReservations } = useContext(ReservationsContext);
  const { customer_workout_plan, getActiveCustomerWorkoutPlan } = useContext(
    CustomerWorkoutPlansContext
  );
  const { isPresencial, canRedeemRewards } = useRewards();

  useEffect(() => {
    if (customer_workout_plan === null) {
      getActiveCustomerWorkoutPlan();
    }
    if (!Array.isArray(reservations)) {
      getMyReservations();
    }
    setupTooltips();
    handleDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      startDate &&
      endDate &&
      Array.isArray(reservations) &&
      Array.isArray(playbacks)
    ) {
      handleDays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer_workout_plan, reservations, playbacks, startDate, endDate]);

  useEffect(() => {
    if (!start_date && !end_date) {
      getPlaybacksByDate(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    if (typeof modifier === "function") {
      modifier(weekIndex, daysCompleted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysCompleted]);

  useEffect(() => {
    // Initialize tooltips after components are rendered
    if (daysComponents) {
      setupTooltips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysComponents]);

  const getProgressValue = (frequency) => {
    return parseInt((daysCompleted / frequency) * 100);
  };

  const getColorClassName = (frequency) => {
    if (start_date && start_date !== null) {
      if (moment(start_date).isAfter(moment().endOf("isoWeek"))) {
        return "light";
      }
    }
    if (daysCompleted >= frequency) return "success";
    if (daysCompleted + 1 === frequency) return "warning";
    return "danger";
  };

  const handleDates = () => {
    if (start_date && start_date !== null) {
      setStartDate(start_date);
    } else {
      setStartDate(moment().startOf("isoWeek").format("YYYY-MM-DD"));
    }
    if (end_date && end_date !== null) {
      setEndDate(end_date);
    } else {
      setEndDate(moment().endOf("isoWeek").format("YYYY-MM-DD"));
    }
  };

  const hasCompletedDayPresencial = (dateString) => {
    if (Array.isArray(reservations)) {
      const dateReservations = reservations.filter(
        ({ single_class }) =>
          moment(single_class.class_date).format("YYYY-MM-DD") === dateString
      );
      if (dateReservations.length > 0) {
        let attended = dateReservations.filter(({ attend }) => attend);
        return attended.length > 0;
      }
    }
    return false;
  };

  const hasCompletedDayOnline = (dateString) => {
    if (Array.isArray(playbacks)) {
      const currentPlaybacks = playbacks.find(({ day }) => day === dateString);
      if (currentPlaybacks && currentPlaybacks !== null) {
        if (currentPlaybacks.playbacks > 0) return true;
      }
    }
    return false;
  };

  const hasCompletedDay = (dateString) => {
    if (customer_workout_plan && customer_workout_plan !== null) {
      if (
        moment(dateString).isBefore(moment(customer_workout_plan.start_date)) ||
        moment(dateString).isAfter(moment(customer_workout_plan.end_date))
      ) {
        return false;
      }
    }
    const online = hasCompletedDayOnline(dateString);
    const presencial = hasCompletedDayPresencial(dateString);
    return { online, presencial };
  };

  const handleDays = () => {
    let components = [];
    let completed = 0;
    const days = moment(endDate).diff(startDate, "days");
    for (let i = 0; i < days; i++) {
      const current = moment(startDate).add(i, "days").format("YYYY-MM-DD");
      const { online, presencial } = hasCompletedDay(current);
      const dayComplete = online || presencial;
      components.push(
        <div key={current} className="col-2">
          <div
            className={`card no-hover p-3 shadow bg-${
              (isPresencial && presencial && canRedeemRewards) ||
              (!isPresencial && online)
                ? "success"
                : isPresencial && online
                ? "secondary"
                : "light"
            } text-center`}
          >
            <p className="bold mb-2">{moment(current).format("DD MMM")}</p>
            {dayComplete ? (
              <i className="fa fa-check-circle text-white fa-2x" />
            ) : (
              <p className="mb-4 pb-2"></p>
            )}
            {isPresencial && online && !presencial && canRedeemRewards && (
              <i
                className="fa fa-info-circle text-white mt-1"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Recuerda: solo las clases presenciales suman puntos en TBM Rewards."
              />
            )}
            {!canRedeemRewards && isPresencial && (
              <i
                className="fa fa-info-circle text-white mt-1"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Recuerda: Para poder acumular puntos en TBM Rewards, debes tener un paquete activo de 12 clases o más."
              />
            )}
          </div>
        </div>
      );
      if (dayComplete) completed++;
    }
    setDaysComponents(components);
    setDaysCompleted(completed);
  };

  const renderWeekIndex = () => {
    if (weekIndex && weekIndex !== null) {
      return `S${weekIndex} -`;
    }
  };

  const renderDays = () => {
    if (
      Array.isArray(daysComponents) &&
      customer_workout_plan &&
      customer_workout_plan !== null
    ) {
      const { frequency } = customer_workout_plan.workout_plan;
      const beforePlanStart = moment()
        .utc()
        .isBefore(moment(customer_workout_plan.start_date).utc());
      const afterPlanEnd = moment()
        .utc()
        .isAfter(moment(customer_workout_plan.end_date).utc());

      return (
        <div className="container-fluid px-0 mt-3">
          {beforePlanStart && (
            <p className="mb-3">
              Prepárate, tu Workout Planner comienza el{" "}
              <span className="bold text-primary">
                {moment(customer_workout_plan.start_date)
                  .utc()
                  .format("DD MMM YYYY")}
              </span>
            </p>
          )}
          {afterPlanEnd && (
            <p className="mb-3">
              Has terminado tu último Workout Planner. Inicia uno nuevo{" "}
              <Link to="/pages/workout-planner" className="text-primary bold">
                aquí.
              </Link>
            </p>
          )}
          <div className="row align-items-center">
            <div className={`${hideHeader ? "col-8 col-md-10" : "col-12"}`}>
              <ProgressBar
                progress={getProgressValue(frequency)}
                color={getColorClassName(frequency)}
              />
            </div>
            {hideHeader && (
              <div className="col-4 col-md-2">{renderFrequency()}</div>
            )}
          </div>
          <div className="streak-week-container py-3">
            <div className="row streak-week mt-3">{daysComponents}</div>
          </div>
        </div>
      );
    }
    if (!customer_workout_plan) {
      return (
        <div className="container-fluid px-0 mt-3">
          <div className="row align-items-center">
            <div className={`${hideHeader ? "col-8 col-md-10" : "col-12"}`}>
              <ProgressBar progress={0} color="light" />
            </div>
            {hideHeader && (
              <div className="col-4 col-md-2">{renderFrequency()}</div>
            )}
          </div>
          {Array.isArray(daysComponents) && daysComponents.length > 0 && (
            <div className="streak-week-container py-3">
              <div className="row streak-week mt-3">{daysComponents}</div>
            </div>
          )}
          {!hideHeader && (
            <div className="mt-3">
              <p>
                Comienza por crear un Workout Planner para rastrear tus logros.
              </p>
              <Link to="/pages/workout-planner" className="btn btn-primary">
                Crear un Workout Planner
              </Link>
            </div>
          )}
        </div>
      );
    }
  };

  const renderFrequency = () => {
    if (customer_workout_plan && customer_workout_plan !== null) {
      const { frequency } = customer_workout_plan.workout_plan;
      return (
        <p
          className={`br-10 text-center mb-0 small bold px-2 py-1 bg-${getColorClassName(
            frequency
          )}`}
        >
          {renderWeekIndex()} {getProgressValue(frequency)}%
        </p>
      );
    }
    return (
      <p className="br-10 text-center mb-0 small bold px-2 py-1 bg-light">
        {renderWeekIndex()} 0%
      </p>
    );
  };

  const renderMonth = () => {
    if (customer_workout_plan && customer_workout_plan !== null) {
      return (
        <button
          onClick={() => handleWorkoutPlanner(customer_workout_plan)}
          className="btn btn-sm btn-primary"
        >
          Ir a Workout Planner
        </button>
      );
    }
  };

  return (
    <div className="container-fluid mt-2 mb-5">
      {!hideHeader && (
        <div className="row mb-5 align-items-center">
          <div className="col-12 col-md-8">
            <h2 className="h3 mb-0">
              Mi Workout Planner Semanal <InlineStreaks />
            </h2>
            <p className="small mb-0">
              Cada día que realices una clase de TBM Online o Presencial se
              contará como un objetivo alcanzado.
            </p>
          </div>
          <div className="col-12 col-md-4 text-end hide-mobile">
            <button
              onClick={() => navigate("/rewards")}
              className="btn border-0 me-2 bold"
              style={{ backgroundColor: "#FFB66B" }}
            >
              TBM Rewards
            </button>
            {renderMonth()}
          </div>
        </div>
      )}
      {renderDays()}
      <div className="mt-3 show-mobile">{renderMonth()}</div>
    </div>
  );
};

export default StreakWeek;
